import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AllergicRhinitisDocTypes,
  checkAMRFormEnable,
  checkFormEnable,
  GetReferralDetails,
  ListOfAMRForms,
  ListOfForms,
  MedicationReviewDocTypes,
} from "../service-steps/documentation/data";
import { minorAilments as minorAilmentsData } from "../../text/data";
import { getTreatmentDrugs } from "../service-steps/treatment/data";
import { useMemo } from "react";
import NewToolTip from "../../../common/New-ToolTip";

const COMPLETED_STATUSES = ["completed", "complete", "done", "cancelled"];

const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;
const isNonEmptyObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

const isAlternatePharmacyLocation = (location) => {
  const normalized = String(location || "").trim().toLowerCase();
  return normalized.includes("alternate");
};

const hasAlternatePharmacyInList = (list = []) => {
  if (!Array.isArray(list)) return false;
  return list.some((entry) => {
    if (!entry) return false;
    if (isAlternatePharmacyLocation(entry)) return true;
    if (isAlternatePharmacyLocation(entry?.pharmacyLocation)) return true;
    if (isAlternatePharmacyLocation(entry?.pharmacy?.location)) return true;
    return false;
  });
};


const isResolvedStatus = (status) =>
  COMPLETED_STATUSES.includes(String(status || "").trim().toLowerCase());

const hasIntervention = (medication) => {
  const intervention = medication?.intervention;

  if (Array.isArray(intervention)) return intervention.length > 0;
  if (intervention && typeof intervention === "object") {
    return Object.keys(intervention).length > 0;
  }

  return Boolean(intervention || medication?.interventionId);
};

const getAMRMedicationList = (record) => {
  const candidates = [
    record?.medications,
    record?.documentation?.medications,
    record?.medicationReview?.medications,
    record?.initialAssessment?.initialAssessmentForm?.medications,
    record?.initialAssessment?.initialAssessmentForm?.documentation?.medications,
    record?.initialAssessment?.medications,
  ];

  for (const value of candidates) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.medications)) return value.medications;
  }

  return [];
};

const hasNotepadData = ({
  record,
  formsNotepad,
  notepad,
  notesResponse,
  medicalSummaryRecord,
}) =>
  Boolean(
    isNonEmptyArray(record?.notepad) ||
      isNonEmptyArray(record?.notes) ||
      isNonEmptyArray(record?.documentation?.notepad) ||
      isNonEmptyArray(record?.notePads) ||
      isNonEmptyArray(record?.notePad) ||
      isNonEmptyArray(record?.notepads) ||
      isNonEmptyArray(record?.documentation?.notePads) ||
      isNonEmptyArray(record?.notesResponse) ||
      isNonEmptyArray(record?.formsNotepad) ||
      isNonEmptyObject(record?.notepad) ||
      isNonEmptyObject(record?.notes) ||
      isNonEmptyObject(record?.notePads) ||
      isNonEmptyObject(record?.notePad) ||
      isNonEmptyObject(record?.notepads) ||
      isNonEmptyObject(record?.documentation?.notepad) ||
      isNonEmptyObject(record?.documentation?.notePads) ||
      isNonEmptyObject(record?.notesResponse) ||
      isNonEmptyObject(record?.formsNotepad) ||
      isNonEmptyArray(formsNotepad) ||
      isNonEmptyArray(notepad) ||
      isNonEmptyArray(notesResponse) ||
      isNonEmptyObject(medicalSummaryRecord?.notesResponse)
  );

const FormIcons = ({ selected = false, medicalSummaryObject = null, currentTabIndex = 0, rowId = null }) => {
  const { id: patientId, summaryId, type } = useParams();
  const navigate = useNavigate();
  const patientsState = useSelector((state) => state.patients || {});
  const { patient, formsNotepad, notepad } = patientsState;
  const medicalSummaryState = useSelector((state) => state.medicalSummary || {});
  const { medicalSummaryRecord, notesResponse } = medicalSummaryState;
  const interventionsFromStore = useSelector((state) => state.intervention?.interventions ?? []);
  const interventionsList = useMemo(() => {
    if (Array.isArray(interventionsFromStore)) return interventionsFromStore;
    if (interventionsFromStore && Array.isArray(interventionsFromStore.items)) return interventionsFromStore.items;
    if (interventionsFromStore && Array.isArray(interventionsFromStore.data)) return interventionsFromStore.data;
    if (interventionsFromStore && Array.isArray(interventionsFromStore.results)) return interventionsFromStore.results;
    return [];
  }, [interventionsFromStore]);

  const serviceType =
    medicalSummaryObject?.minorAilmentTypeCode ||
    medicalSummaryObject?.medicationReviewTypeCode;
  const isMA = Boolean(medicalSummaryObject?.minorAilmentTypeCode);
  const conditionTitle = minorAilmentsData?.find(
    (item) => item.code?.toLowerCase() === String(medicalSummaryObject?.minorAilmentTypeCode || "").toLowerCase()
  )?.title;
  const condition = conditionTitle === "Allergic Rhinitis" ? "Allergic rhinitis" : conditionTitle;

  const handleIconClick = (formType, enabled) => {
    if (!selected || !enabled) {
      return;
    }

    const recordSummaryId =
      medicalSummaryObject?.id ||
      medicalSummaryObject?.minorAilmentId ||
      medicalSummaryObject?.medicationReviewId ||
      (summaryId && summaryId !== "undefined" && summaryId !== "null" ? summaryId : null);

    if (!recordSummaryId) {
      return;
    }

    try {
      localStorage.setItem(`medicalSummaryTab_${recordSummaryId}`, currentTabIndex.toString());
    } catch (err) {
      void err;
    }

    const rawServiceDate =
      medicalSummaryObject?.systemPeriod?.LowerBound ||
      medicalSummaryObject?.date ||
      medicalSummaryObject?.systemPeriod;
    const serviceDateIso = rawServiceDate ? new Date(rawServiceDate).toISOString().split("T")[0] : null;
    const dateQuery = serviceDateIso ? `serviceDate=${encodeURIComponent(serviceDateIso)}` : "";
    const selectedRowQuery = rowId ? `selectedRowId=${encodeURIComponent(rowId)}` : "";
    const queries = [selectedRowQuery, dateQuery].filter(Boolean).join("&");
    const queryString = queries ? `?${queries}` : "";
    const url = `/patient/${patientId}/medical-summary/${type}/${recordSummaryId}/form/${formType}${queryString}`;

    navigate(url);
  };

  const getFormConfig = (formType, fallbackName) => {
    const fromMA = ListOfForms.find((form) => form.type === formType);
    const fromAMR = ListOfAMRForms.find((form) => form.type === formType);
    const form = fromMA || fromAMR;

    return {
      activeIcon: form?.activeIcon || "",
      inactiveIcon: form?.inActiveIcon || form?.activeIcon || "",
      name: form?.name || fallbackName || "",
    };
  };

  const findInterventionsForMedicationRow = (medication) => {
    if (!medication || !Array.isArray(interventionsList)) return [];

    const idSet = new Set(
      [medication.id, medication.medicationId].filter(Boolean).map((x) => String(x))
    );

    return interventionsList.filter((inv) => {
      if (inv?.medicationId != null && idSet.has(String(inv.medicationId))) {
        return true;
      }
      if (medication?.intervention?.id && inv?.id === medication.intervention.id) return true;
      if (medication?.interventionId && inv?.id === medication.interventionId) return true;
      return false;
    });
  };

  const isInterventionPending = (medication) => {
    if (!medication) return false;

    const matchedInterventions = findInterventionsForMedicationRow(medication);
    if (matchedInterventions.length > 0) {
      return matchedInterventions.some((inv) => !isResolvedStatus(inv?.status));
    }

    if (!hasIntervention(medication)) return false;

    return !isResolvedStatus(medication?.status);
  };

  const getPMRReviewState = (record) => {
    const medicationList = getAMRMedicationList(record);
    const hasMedicationData = medicationList.length > 0;
    const hasActiveInterventions = medicationList.some((medication) => isInterventionPending(medication));

    return {
      hasMedicationData,
      hasActiveInterventions,
    };
  };

  const buildMinorAilmentIcons = (record, currentCondition) => {
    if (currentCondition !== "Allergic rhinitis") return [];

    const initialAssessmentForm = record?.initialAssessment?.initialAssessmentForm;
    const alertData = GetReferralDetails(initialAssessmentForm);
    const treatment = initialAssessmentForm?.treatment;
    const prescriptionDrugs = getTreatmentDrugs(treatment);
    const dateOfBirth = patient?.dateOfBirth;
    const documentation = initialAssessmentForm?.documentation;

    const isTypeEnabled = (docType) =>
      checkFormEnable(docType, alertData, prescriptionDrugs, dateOfBirth, documentation);

    const hasInitialAssessment = Boolean(initialAssessmentForm);
    const hasPrescriptions = isNonEmptyArray(prescriptionDrugs);
    const hasAlternatePharmacyPrescription = prescriptionDrugs.some(
      (drug) =>
        isAlternatePharmacyLocation(drug?.pharmacyLocation) ||
        isAlternatePharmacyLocation(drug?.pharmacy?.location)
    );
    const hasAlternatePharmacyInDocumentation = isAlternatePharmacyLocation(
      documentation?.pharmacyLocation
    );
    const hasAlternatePharmacyInRecord = isAlternatePharmacyLocation(
      initialAssessmentForm?.pharmacyLocation
    );
    const hasAlternatePharmacyInReferralInfo =
      hasAlternatePharmacyInList(documentation?.referralInformations) ||
      hasAlternatePharmacyInList(documentation?.notificationInformations);
    const hasAlternatePharmacySelection =
      hasAlternatePharmacyPrescription ||
      hasAlternatePharmacyInDocumentation ||
      hasAlternatePharmacyInRecord ||
      hasAlternatePharmacyInReferralInfo;

    const hasStoryboard = Boolean(initialAssessmentForm?.treatment);
    const hasNotepad = hasNotepadData({
      record,
      formsNotepad,
      notepad,
      notesResponse,
      medicalSummaryRecord,
    });

    const prescribingType = isTypeEnabled(AllergicRhinitisDocTypes.PrescribingRecordWithReferral)
      ? AllergicRhinitisDocTypes.PrescribingRecordWithReferral
      : isTypeEnabled(AllergicRhinitisDocTypes.PrescribingRecord)
      ? AllergicRhinitisDocTypes.PrescribingRecord
      : AllergicRhinitisDocTypes.PhysicianReferralForm;

    const iconOrder = [
      AllergicRhinitisDocTypes.InitialAssessment,
      prescribingType,
      AllergicRhinitisDocTypes.Prescription,
      AllergicRhinitisDocTypes.Storyboard,
      AllergicRhinitisDocTypes.Notepad,
    ];

    const hasPatientReferralData =
      isNonEmptyArray(documentation?.referralType) ||
      isNonEmptyArray(documentation?.referralInformations);
    const isEndServiceAndRefer = Boolean(alertData?.EndServiceAndRefer);
    const hasCommunicationForBillingData =
      (documentation?.billingInformation &&
        String(documentation.billingInformation).trim().length > 0) ||
      isNonEmptyArray(documentation?.notificationType) ||
      isNonEmptyArray(documentation?.notificationInformations);

    if (isEndServiceAndRefer) {
      if (!iconOrder.includes(AllergicRhinitisDocTypes.PatientReferral)) {
        iconOrder.push(AllergicRhinitisDocTypes.PatientReferral);
      }
      if (!iconOrder.includes(AllergicRhinitisDocTypes.PhysicianReferralForm)) {
        iconOrder.push(AllergicRhinitisDocTypes.PhysicianReferralForm);
      }
      if (!iconOrder.includes(AllergicRhinitisDocTypes.CommunicationForBilling)) {
        iconOrder.push(AllergicRhinitisDocTypes.CommunicationForBilling);
      }
    } else {
      if (hasPatientReferralData) {
        iconOrder.push(AllergicRhinitisDocTypes.PatientReferral);
      }
      if (hasCommunicationForBillingData) {
        iconOrder.push(AllergicRhinitisDocTypes.CommunicationForBilling);
      }
    }

    return iconOrder
      .map((formType, idx) => {
        let formConfig = getFormConfig(formType, "");
        let enabled = isTypeEnabled(formType);
        let tooltip = formConfig.name;

        if (formType === AllergicRhinitisDocTypes.CommunicationForBilling) {
          if (isEndServiceAndRefer && !hasPrescriptions) {
            formConfig = {
              ...formConfig,
              activeIcon: "/icons/medicalSummary/MA/CommunicationForBillingNoPresActive.svg",
              inactiveIcon: "/icons/medicalSummary/MA/CommunicationForBillingNoPresInActive.svg",
            };
            tooltip = "End Service & Refer\nBill for assessment - No Prescriptions";
          } else if (hasAlternatePharmacySelection) {
            formConfig = {
              ...formConfig,
              activeIcon: "/icons/medicalSummary/MA/CommunicationForBillingAlterPresActive.svg",
              inactiveIcon: "/icons/medicalSummary/MA/CommunicationForBillingAlterPresInActive.svg",
            };
            tooltip = "Rx issued- Fill at Alternate Pharmacy\nBill for service only";
          }
        }

        if (formType === AllergicRhinitisDocTypes.InitialAssessment) {
          tooltip = "Pharmacist Initial Assessment";
          if (!hasInitialAssessment) {
            enabled = false;
            tooltip = "Pharmacist Initial Assessment\nNo assessment completed";
          }
        }

        if (formType === AllergicRhinitisDocTypes.PrescribingRecord) {
          tooltip = "Pharmacist Prescribing Record";
        }

        if (formType === AllergicRhinitisDocTypes.PrescribingRecordWithReferral) {
          tooltip = "Pharmacist Prescribing Record + Referral";
        }

        if (formType === AllergicRhinitisDocTypes.PhysicianReferralForm) {
          tooltip = "Physician Referral";
        }

        if (formType === AllergicRhinitisDocTypes.Prescription && !hasPrescriptions) {
          enabled = false;
          tooltip = "Prescriptions - No Prescriptions Issued";
        }

        if (formType === AllergicRhinitisDocTypes.Storyboard && !hasStoryboard) {
          enabled = false;
          tooltip = "Storyboard - No Storyboard Created";
        }

        if (formType === AllergicRhinitisDocTypes.Notepad) {
          enabled = true;
          if (!hasNotepad) {
            tooltip = "Notepad Notes";
          }
        }

        if (
          (formType === AllergicRhinitisDocTypes.PatientReferral ||
            formType === AllergicRhinitisDocTypes.CommunicationForBilling) &&
          !enabled &&
          !isEndServiceAndRefer
        ) {
          return null;
        }

        return {
          key: `${formType}-${record?.id || rowId || "row"}-${idx}`,
          enabled,
          tooltip,
          activeIcon: formConfig.activeIcon,
          inactiveIcon: formConfig.inactiveIcon,
          onClick: () => handleIconClick(formType, enabled),
        };
      })
      .filter(Boolean);
  };

  const buildMedicationReviewIcons = (record) =>
    ListOfAMRForms.slice(1).map((form, idx) => {
      const isPMR = form.type === MedicationReviewDocTypes.PersonalMedRecord;
      const isStoryboard = form.type === MedicationReviewDocTypes.StoryBoard || String(form?.name || "").trim().toLowerCase() === "storyboard";
      const { hasMedicationData, hasActiveInterventions } = getPMRReviewState(record);
      const enabled = isStoryboard ? false : isPMR ? hasMedicationData : checkAMRFormEnable(form.type, false);
      const isInReview = isPMR && hasMedicationData && hasActiveInterventions;
      const baseTooltip = isStoryboard ? "Storyboard - Coming Soon" : form.name;

      return {
        key: `${form.type}-${record?.id || rowId || "row"}-${form.id ?? ""}-${idx}`,
        enabled,
        tooltip: isInReview ? `${form.name} (In Review)` : baseTooltip,
        activeIcon: form.activeIcon,
        inactiveIcon: form.inActiveIcon,
        isInReview,
        onClick: () => handleIconClick(form.type, enabled),
      };
    });

  const buildIcons = (record, currentCondition, isMinorAilment) => {
    if (!record || !serviceType) return [];
    if (isMinorAilment) return buildMinorAilmentIcons(record, currentCondition);
    return buildMedicationReviewIcons(record);
  };

  const icons = buildIcons(medicalSummaryObject, condition, isMA);
  const renderIconButton = (icon) => {
    const tooltipId = `form-tooltip-${icon.key}`;

    return (
      <div key={icon.key} className="relative">
        {icon.isInReview && (
          <span
            className="absolute -top-1 -right-1 z-10 h-2.5 w-2.5 rounded-full bg-yellow-100 ring-1 ring-teal-300 pointer-events-none"
            aria-hidden
          />
        )}

        <button
          className={`w-10 h-10 flex items-center justify-center ${icon.enabled ? "" : "cursor-not-allowed"}`}
          type="button"
          data-tooltip-id={tooltipId}
          onClick={(e) => {
            e.stopPropagation();
            icon.onClick();
          }}
        >
          <img
            src={selected && icon.enabled ? icon.activeIcon : icon.inactiveIcon}
            alt={icon.tooltip || "form-icon"}
            className="w-10 h-10"
            draggable={false}
          />
        </button>

        <NewToolTip
          id={tooltipId}
          place="top"
          render={() => (
            <div className="bg-white p-2 rounded shadow-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-800 whitespace-pre-line">
                {icon.tooltip}
              </p>
            </div>
          )}
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center gap-4">
        {icons.map(renderIconButton)}
      </div>
    </>
  );
};

export default FormIcons;
